'use client'

import React, { useEffect, useState } from 'react'
import { Dialog, DialogContent, DialogTrigger, DialogTitle } from '@/components/ui/dialog'
import { X, CaretLeft, CaretRight } from 'phosphor-react'
import { Button } from '@/components/ui/button'

interface ImageModalProps {
  src: string
  alt: string
  className?: string
  children?: React.ReactNode
  // Optional carousel controls from parent
  onPrev?: () => void
  onNext?: () => void
  hasPrev?: boolean
  hasNext?: boolean
}

export default function ImageModal({ src, alt, className = "", children, onPrev, onNext, hasPrev, hasNext }: ImageModalProps) {
  const [open, setOpen] = useState(false)

  // Keyboard navigation inside modal
  useEffect(() => {
    if (!open) return
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft' && onPrev) {
        e.preventDefault()
        onPrev()
      } else if (e.key === 'ArrowRight' && onNext) {
        e.preventDefault()
        onNext()
      }
    }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [open, onPrev, onNext])

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <div 
          className={`cursor-pointer hover:opacity-80 transition-opacity duration-200 ${className}`}
          role="button"
          aria-label={`View image: ${alt}`}
          tabIndex={0}
        >
          {children || (
            <img 
              src={src} 
              alt={alt} 
              className="w-full h-full object-cover rounded-lg max-w-full"
              style={{ maxWidth: '100%', height: 'auto' }}
            />
          )}
        </div>
      </DialogTrigger>
      <DialogContent 
        showCloseButton={false}
        className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 bg-black/98 border-2 border-[var(--brutal-ink)] rounded-lg overflow-hidden p-0 !w-[min(96vw,1100px)] !max-w-[96vw] !h-auto max-h-[92vh]"
      >
        <DialogTitle className="sr-only">{alt}</DialogTitle>
        <div className="relative flex items-center justify-center max-h-[92vh] overflow-auto overscroll-contain">
          {/* Close button - positioned in top right */}
          <Button
            variant="ghost"
            size="icon"
            className="absolute top-3 right-3 z-10 bg-black/50 hover:bg-black/70 text-white border-0 rounded-full w-10 h-10"
            onClick={() => setOpen(false)}
            aria-label="Close image modal"
          >
            <X weight="duotone" className="w-5 h-5" />
          </Button>
          
          {/* Actual image with mobile-friendly sizing */}
          <img 
            src={src} 
            alt={alt} 
            className="object-contain rounded-lg w-full h-auto"
            style={{
              maxWidth: '100%',
              maxHeight: '88vh',
              width: 'auto',
              height: 'auto'
            }}
          />

          {/* Modal navigation arrows */}
          {(hasPrev || hasNext) && (
            <>
              {hasPrev && (
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={(e) => { e.stopPropagation(); onPrev?.() }}
                  className="absolute left-4 top-1/2 -translate-y-1/2 bg-black/70 hover:bg-black/90 text-white border border-white/40 hover:border-white shadow-lg backdrop-blur-sm transition-all duration-200 w-10 h-10"
                  aria-label="Previous image"
                >
                  <CaretLeft weight="duotone" className="w-6 h-6" />
                </Button>
              )}
              {hasNext && (
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={(e) => { e.stopPropagation(); onNext?.() }}
                  className="absolute right-4 top-1/2 -translate-y-1/2 bg-black/70 hover:bg-black/90 text-white border border-white/40 hover:border-white shadow-lg backdrop-blur-sm transition-all duration-200 w-10 h-10"
                  aria-label="Next image"
                >
                  <CaretRight weight="duotone" className="w-6 h-6" />
                </Button>
              )}
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  )
}